import { ref, computed, watch } from 'vue';

import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';
import Utils from '@/api/Utils';

import {
	getBaseOptions,
	generateSeries,
	nullSafeFormatter,
	calcMedian,
	aggregateByMinute,
	splitObjectToSeries,
} from '@/utils/chartsUtils';

import { datetimeToString } from '@/components/common/dateUtils';



const CHART_DY = 0.6;
const DATA_DY = 0.5;

const SERIES_NAMES = {
	e_coord: 'E coordinate',
	n_coord: 'N coordinate',
	u_coord: 'U coordinate',
};


const QUALITY_OPTIONS = [
	{
		value: -1,
		key: 'all',
	},
	{
		value: 4,
		key: 'rtkFixed',
	},
	{
		value: 5,
		key: 'rtkFloat',
	},
	{
		value: 10236,
		key: 'others',
	},
];


const DEFAULT_EPOCH_COUNTS = {
	all: 0,
	selected: 0,
	percent: 0,
};


export function useBaselineChartsData(baselineId) {
	const { t } = useI18n();
	const isStatsLoading = ref(false);
	const statsData = ref([]);
	const epochCounts = ref(DEFAULT_EPOCH_COUNTS);

	const selectedQuality = ref(QUALITY_OPTIONS[0].value);
	const qualityOptions = computed(() => QUALITY_OPTIONS.map(option => ({
		value: option.value,
		label: t(`baselines-edit.qualityStatus.${option.key}`),
	})));

	const updateStats = () => {
		isStatsLoading.value = true;
		Api.baselineGetStats(baselineId.value, selectedQuality.value, data => {
			if (data.status === 'ok') {
				statsData.value = data.stats;
				epochCounts.value = data.epoch_counts;
			} else {
				statsData.value = [];
				epochCounts.value = DEFAULT_EPOCH_COUNTS;
			}
			isStatsLoading.value = false;
		});
	};

	watch(selectedQuality, () => {
		updateStats();
	});

	const calcSeriesMedian = seriesData => {
		const nonNullData = seriesData.map(item => item[1]).filter(item => item != null);
		return calcMedian(nonNullData);
	}

	const allSeries = computed(() => {
		const splitted = splitObjectToSeries(statsData.value, 'epoch', ['n_coord', 'e_coord', 'u_coord']);
		const aggregated = aggregateByMinute(splitted, ['n_coord', 'e_coord', 'u_coord']);
		const rawSeries = generateSeries({
			items: aggregated,
			datetimeField: 'epoch',
			getSeriesKeyFn: item => SERIES_NAMES[item.name],
			getValueFn: item => item.value,
			keySorter: Utils.sortArrayAccordingToValuesComparator([SERIES_NAMES.n_coord, SERIES_NAMES.e_coord, SERIES_NAMES.u_coord]),
		}).series;
		return rawSeries.map(seriesItem => {
			let median = calcSeriesMedian(seriesItem.data);
			if (median == null) {
				median = 0;
			}
			const correctedData = seriesItem.data.map(item => {
				const realValue = item[1] != null ? (item[1] - median) : null;
				let correctedValue = realValue;
				if (correctedValue != null) {
					correctedValue = Math.max(Math.min(correctedValue, DATA_DY), -DATA_DY);
				}
				return [item[0], realValue, correctedValue];
			});
			return {
				name: seriesItem.name,
				hidden: seriesItem.hidden,
				data: correctedData.map(item => [item[0], item[2]]),
				realData: correctedData.map(item => [item[0], item[1]]),
				median,
			};
		});
	});
	const allOptions = computed(() => {
		let options = getBaseOptions('all_series', allSeries.value);
		options.yaxis.title.text = 'Meters';
		options.yaxis.min = -CHART_DY;
		options.yaxis.max = CHART_DY;
		options.yaxis.decimalsInFloat = 2;
		options.tooltip.y.formatter = nullSafeFormatter(value => value.toFixed(3) + ' m');
		const tooltipGenerator = ({ dataPointIndex, w }) => {
			const rawSeries = w.config.series;
			let result = '<div class="baseline-neu-popup"><div class="baseline-neu-popup-header">';
			result += '<span class="baseline-neu-datetime">' + datetimeToString(rawSeries[0].data[dataPointIndex][0]) + '</span>';
			result += '</div><div class="baseline-neu-popup-content">';
			for (const [index, series] of rawSeries.entries()) {
				if (series.data.length === 0) {
					continue;
				}
				const name = series.name;
				const data = series.realData[dataPointIndex];
				result += '<div class="baseline-neu-coordinate-row">';
				result += '<span class="baseline-neu-coordinate-dot" style="background-color: ' + w.config.colors[index] + '"></span>';
				result += '<span class="baseline-neu-coordinate-label">' + name + ':</span>';
				const dataValue = (data[1] != null) ? (data[1].toFixed(3) + ' m') : 'N/A';
				result += '<span class="baseline-neu-coordinate-value">' + dataValue + '</span>';
				result += '</div>';
			}
			result += '</div></div>';
			return result;
		};
		options.tooltip.custom = tooltipGenerator;
		return options;
	});

	return {
		allSeries,
		allOptions,
		epochCounts,
		isStatsLoading,
		selectedQuality,
		qualityOptions,
		updateStats,
	};
}
